import { t } from 'i18next';
import { LessonItemFragment, ListPlannedTeacherTeachingLoadQuery } from '../../../types/planung-graphql-client-defs';
import { groupByArray, partition } from '../../../utils/arrayFunc';
import { onlyUnique } from '../../../utils/unique-filter';
import { CardsInfo, CardsInfoLesson, LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';

export const lessonResponseToTableType = ({
  versionContext,
  queryLessonsData,
  deputateByTeachersData,
  isTeachingBlock,
  teachingBlockCards,
  timetableCards,
  subjectsData,
  subjectContainerData,
}: {
  queryLessonsData?: LessonItemFragment[];
  deputateByTeachersData: Pick<
    ListPlannedTeacherTeachingLoadQuery,
    'plannedTeachingLoadByTeacher'
  >['plannedTeachingLoadByTeacher'];
  versionContext: boolean;
  isTeachingBlock?: boolean;
  teachingBlockCards?: {
    uuid: string;
    lesson: { uuid: string };
    duration?: number;
    subject: { uuid: string; name: string; shortName: string };
  }[];
  timetableCards?: {
    uuid: string;
    lesson: { uuid: string };
    duration?: number;
    subject: { uuid: string; name: string; shortName: string };
  }[];
  subjectsData?: { uuid: string; name: string; shortName: string }[];
  subjectContainerData?: { uuid: string; name: string; shortName: string }[];
}) => {
  const lessonsData: LessonTableType[] =
    queryLessonsData?.map((lesson): LessonTableType => {
      /*
        VORSICHT !
        versionsAggregate = aktive Stundenpläne
        timetableCardsAggregate = gesteckte Karten
       */

      const [firstLessonClass] = lesson.lessonClasses;
      const lessonClasses: CardsInfoLesson | null = firstLessonClass
        ? {
            uuid: lesson.uuid,
            usedDivision: firstLessonClass.usedDivision,
            groups: firstLessonClass.groups,
          }
        : null;
      const teacherColors = lesson.teachers.map((teacher) => teacher.color ?? '#fff');

      // Server-seitig vorberechnet (BE-Resolver via DataLoader); ersetzt das fruehere FE-getStatus.
      // status steuert das Gating, reasons/references liefern das "Warum" fuer die Meldung.
      const statusInfo = versionContext ? lesson.editStatusInVersion : lesson.editStatus;

      const data: LessonTableType = {
        classSort: lesson.classSort,
        onlyInTimetableVersion: lesson.onlyInTimetableVersion,
        subject: lesson.subject.name,
        subjectShortName: lesson.subject.shortName,
        subjectUuid: lesson.subject.uuid,
        editStatus: statusInfo?.status ?? undefined,
        editStatusReasons: statusInfo?.reasons ?? [],

        uuid: lesson.uuid,
        classes: lesson.lessonClasses.map((lessonClass) => {
          return {
            uuid: lessonClass.class.uuid,
            name: lessonClass.class.shortName,
            shortName: lessonClass.class.shortName,
            grade: lessonClass.class.grade ?? 0,
            gradeGroup: lessonClass.class.gradeGroup,
            groups: lessonClass.groups.map((group) => ({
              ...group,
              // Anteil an der Klasse: eins durch die Zahl der Gruppen der verwendeten Teilung.
              classFraction: 1 / (lessonClass.usedDivision?.groups.length ?? 1),
            })),
          };
        }),
        classesUuids: lesson.lessonClasses.map((lessonClass) => lessonClass.class.uuid),
        teachers: lesson.teachers.map((teacher) => {
          return {
            uuid: teacher.uuid,
            name: teacher.displayNameShort || teacher.shortName,
            color: teacher.color,
            teachingLoadEnabled: teacher.teachingLoad ?? false,
            presenceEnabled: teacher.present ?? false,
            teachingLoadHours: teacher.teachingLoadHours ?? undefined,
          };
        }),
        teacherUuids: lesson.teachers.map((teacher) => teacher.uuid),
        elective: lesson.elective ?? false,
        teachingLoadEnabled: lesson.teachingLoadEnabled ?? false,
        timetableEnabled: lesson.timetableEnabled ?? false,
        lessonUnits: lesson.lessonUnits.map((lessonUnit) => {
          return {
            uuid: lessonUnit.uuid,
            duration: lessonUnit.duration ?? 0,
            count: lessonUnit.count ?? 0,
            subjectContainer: lessonUnit.subjectContainer,
          };
        }),
        groupClassNames: lesson.groupClassNames ?? '',
        placedCardsCount: (lesson.placedCardsCount ?? 0) + (lesson.teachingBlockPlacedCardsCount ?? 0),
        isSubjectContainerLesson: lesson.subject.isSubjectContainer,
        curriculumUuid: lesson.curriculumUuid ?? undefined,
        schoolYearUuid: lesson.schoolYearUuid ?? undefined,
        rooms: lesson.roomSupply.map((room) => ({ uuid: room.uuid, name: room.name })),
        roomsUuids: lesson.roomSupply.map((room) => room.uuid),
      };

      const relevantTeacherTeachingLoad = deputateByTeachersData.filter((d) => d.lessonUuid === lesson.uuid);
      data.teachingLoadInfo = {
        deputatByTeacher: new Map<string, number>(),
        deputatByClass: new Map<string, number>(),
        deputat: 0,
        fachstundenDeputat: 0,
        epochenDeputat: 0,
        studentHours: 0,
        teacherHours: 0,
        teachingBlocks: 0,
        teachingBlockWeeks: 0,
      };
      data.teachingLoadInfo = relevantTeacherTeachingLoad.reduce((agg, rtl) => {
        agg.deputat += rtl.deputat;
        agg.studentHours = rtl.studentHours ?? 0;
        agg.teacherHours = rtl.teacherHours ?? 0;
        agg.teachingBlocks = rtl.teachingBlocks ?? 0;
        agg.teachingBlockWeeks = rtl.teachingBlockWeeks ?? 0;
        rtl.lessonClass.forEach((lc) => {
          const currentClassDeputat = agg.deputatByClass.get(lc.class.uuid) ?? 0;

          agg.deputatByClass.set(
            lc.class.uuid,
            rtl.deputat / rtl.lessonClass.map((lc) => lc.class.uuid).filter(onlyUnique).length + currentClassDeputat,
          );
        });
        const currentDeputat = agg.deputatByTeacher.get(rtl.teacherUuid) ?? 0;
        agg.deputatByTeacher.set(rtl.teacherUuid, rtl.deputat + currentDeputat);
        return agg;
      }, data.teachingLoadInfo);

      if (!isTeachingBlock && timetableCards) {
        const _timetableCards = timetableCards?.filter((card) => card.lesson.uuid === lesson.uuid) ?? [];
        const _teachingBlockCards = teachingBlockCards?.filter((card) => card.lesson.uuid === lesson.uuid) ?? [];
        const defaultDurationGroups = groupByArray(_timetableCards, (card) => card.duration ?? 1);
        const subjectContainerDurationGroups = groupByArray(_teachingBlockCards, (card) => card.duration ?? 1);
        data.cardsInfos = {
          defaultCards: defaultDurationGroups
            ? defaultDurationGroups.map((group) => {
                const subject = subjectsData?.find((s) => s.uuid === group[0].subject.uuid);
                const subjectContainer = subjectContainerData?.find((s) => s.uuid === group[0].subject.uuid);

                return group.map((card) => {
                  return {
                    uuid: card.uuid,
                    type: 'FS' as const,
                    typeLabel: t('FS'),
                    typeLabelShort: t('FS'),
                    cardLabel: subject?.name ?? subjectContainer?.name ?? '',
                    cardLabelShort: subject?.shortName ?? subjectContainer?.shortName ?? '',
                    colors: teacherColors,
                    counter: group.length,
                    lesson: lessonClasses,
                    duration: card.duration ?? 1,
                  };
                });
              })
            : [],
          subjectContainerCards: subjectContainerDurationGroups
            ? subjectContainerDurationGroups.map((group) => {
                const subjectContainer = subjectContainerData?.find((s) => s.uuid === group[0].subject.uuid);

                return group.map((card) => {
                  return {
                    uuid: card.uuid,
                    type: 'Container' as const,
                    typeLabel: subjectContainer?.name ?? '',
                    typeLabelShort: subjectContainer?.shortName ?? '',
                    cardLabel: `${card.duration} ${t('common.week', { count: card.duration ?? 1 })}`,
                    cardLabelShort: `${card.duration} ${t('common.week', { count: card.duration ?? 1 })}`,
                    colors: teacherColors,
                    counter: group.length,
                    lesson: lessonClasses,
                    duration: card.duration ?? 1,
                  };
                });
              })
            : [],
        };
      } else if (isTeachingBlock && teachingBlockCards) {
        const _teachingBlockCards = teachingBlockCards?.filter((card) => card.lesson.uuid === lesson.uuid) ?? [];
        const subjectContainerDurationGroups = groupByArray(_teachingBlockCards, (card) => card.duration ?? 1);

        data.cardsInfos = {
          defaultCards: [],
          subjectContainerCards: subjectContainerDurationGroups
            ? subjectContainerDurationGroups.map((group) => {
                const subject = subjectsData?.find((s) => s.uuid === group[0].subject.uuid);

                return group.map((card) => {
                  return {
                    uuid: card.uuid,
                    type: 'Container' as const,
                    typeLabel: subject?.name ?? '',
                    typeLabelShort: subject?.shortName ?? '',
                    cardLabel: `${card.duration} ${t('common.week', { count: card.duration ?? 1 })}`,
                    cardLabelShort: `${card.duration} ${t('common.week', { count: card.duration ?? 1 })}`,
                    colors: teacherColors,
                    counter: group.length,
                    lesson: lessonClasses,
                    duration: card.duration ?? 1,
                  };
                });
              })
            : [],
        };
      } else {
        const [subjectContainers, defaultLessons] = partition(lesson.lessonUnits, (unit) => !!unit.subjectContainer);

        const subjectContainersDurationGroups = groupByArray(
          subjectContainers,
          (lessonUnit) => lessonUnit.duration ?? 1,
        );
        const defaultLessonsDurationGroups = groupByArray(defaultLessons, (lessonUnit) => lessonUnit.duration ?? 1);

        const defaultLessonsCardData = defaultLessonsDurationGroups.map((group) => {
          const count = group
            .map((g) => g.count)
            .reduce((previousValue, currentValue) => {
              return (previousValue ?? 0) + (currentValue ?? 0);
            }, 0);

          return group.map((lessonUnit) => {
            const card: CardsInfo = {
              uuid: `${lessonUnit.uuid}-${lessonUnit.duration}-${lesson.subject.uuid}`,
              type: 'FS',
              typeLabel: t('FS'),
              typeLabelShort: t('FS'),
              cardLabel: lesson.subject.name,
              colors: teacherColors,
              counter: count ?? 1,
              lesson: lessonClasses,
              cardLabelShort: lesson.subject.shortName,
              duration: lessonUnit.duration ?? 1,
            };
            return card;
          });
        });

        const subjectContainersCardData = subjectContainersDurationGroups.map((group) => {
          const count = group
            .map((g) => g.count)
            .reduce((previousValue, currentValue) => {
              return (previousValue ?? 0) + (currentValue ?? 0);
            }, 0);
          return group.map((lessonUnit) => {
            const card: CardsInfo = {
              uuid: `${lessonUnit.uuid}-${lessonUnit.duration}-${lesson.subject.uuid}`,
              type: 'Container',
              typeLabel: lessonUnit.subjectContainer?.name ?? '',
              typeLabelShort: lessonUnit.subjectContainer?.shortName ?? '',
              cardLabel: `${lessonUnit.duration} ${t('common.week', { count: lessonUnit.duration ?? 1 })}`,
              cardLabelShort: `${lessonUnit.duration} ${t('common.week', { count: lessonUnit.duration ?? 1 })}`,
              colors: teacherColors,
              counter: count ?? 1,
              lesson: lessonClasses,
              duration: lessonUnit.duration ?? 1,
            };
            return card;
          });
        });

        data.cardsInfos = { defaultCards: defaultLessonsCardData, subjectContainerCards: subjectContainersCardData };
      }

      return data;
    }) ?? [];
  return lessonsData;
};
